import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cacheOrgData, cacheOrgSettings, cacheUserSettings } from '@/lib/offlineSync';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [sessionToken, setSessionToken] = useState(localStorage.getItem('session_token'));
  const [isLoading, setIsLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Refs to prevent duplicate calls
  const verifyingRef = useRef(false);
  const loadingSessionRef = useRef(false);
  const hasShownLoginToast = useRef(false);

  // Check for magic link token in URL (only on specific routes)
  useEffect(() => {
    const token = searchParams.get('token');
    const pathname = window.location.pathname;
    
    // Only verify magic links on /auth/verify or /demo routes
    const isMagicLinkRoute = pathname === '/auth/verify' || pathname === '/demo';
    
    if (token && isMagicLinkRoute && !verifyingRef.current) {
      verifyMagicLink(token);
    }
  }, [searchParams]);

  // Load session on mount
  useEffect(() => {
    if (sessionToken && !loadingSessionRef.current) {
      loadSession();
    } else if (!sessionToken) {
      setIsLoading(false);
    }
  }, [sessionToken]);

  const loadSession = async () => {
    if (loadingSessionRef.current) return;
    loadingSessionRef.current = true;
    
    try {
      const response = await api.get(`/auth/session?token=${sessionToken}`);
      setUser(response.data.user);
      setOrganization(response.data.organization);
      setPermissions(response.data.permissions || null);
      
      // Cache data for offline use (in background)
      cacheDataForOffline(response.data.organization, response.data.user);
    } catch (error) {
      console.error('Session load failed:', error);
      localStorage.removeItem('session_token');
      setSessionToken(null);
    } finally {
      setIsLoading(false);
      loadingSessionRef.current = false;
    }
  };
  
  // Cache data for offline use (runs in background, doesn't block)
  const cacheDataForOffline = async (org, userData) => {
    try {
      if (!org?.id) return;
      console.log('[Auth] Caching data for offline use...');
      
      // Cache org and user settings
      await cacheOrgSettings({ ...org, id: org.id });
      if (userData) {
        await cacheUserSettings(userData);
      }
      
      // Cache main data (runs in background)
      cacheOrgData(org.id).then(() => {
        console.log('[Auth] Offline data cached successfully');
      }).catch(err => {
        console.warn('[Auth] Background cache failed (non-critical):', err);
      });
    } catch (error) {
      console.warn('[Auth] Failed to cache offline data:', error);
      // Don't throw - caching is non-critical
    }
  };

  const verifyMagicLink = async (token) => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    
    try {
      setIsLoading(true);
      const response = await api.get(`/auth/verify?token=${token}`);
      
      const { session_token, user: userData, organization: orgData, permissions: permsData } = response.data;
      
      localStorage.setItem('session_token', session_token);
      setSessionToken(session_token);
      setUser(userData);
      setOrganization(orgData);
      setPermissions(permsData || null);
      
      // Cache data for offline use (in background)
      cacheDataForOffline(orgData, userData);
      
      // Only show toast once
      if (!hasShownLoginToast.current) {
        hasShownLoginToast.current = true;
        toast.success('Welcome back!', { 
          description: `Logged in as ${userData.name}`,
          id: 'login-success' // Unique ID prevents duplicates
        });
      }
      
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Magic link verification failed:', error);
      toast.error('Login failed', { 
        description: 'Invalid or expired login link',
        id: 'login-error'
      });
      navigate('/login', { replace: true });
    } finally {
      setIsLoading(false);
      // Reset after a delay to allow for future logins
      setTimeout(() => {
        verifyingRef.current = false;
        hasShownLoginToast.current = false;
      }, 1000);
    }
  };

  const requestMagicLink = async (email) => {
    try {
      const response = await api.post('/auth/magic-link', { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/login', { email, password });
      
      const { session_token, user: userData, organization: orgData, permissions: permsData } = response.data;
      
      localStorage.setItem('session_token', session_token);
      setSessionToken(session_token);
      setUser(userData);
      setOrganization(orgData);
      setPermissions(permsData || null);
      
      // Cache data for offline use (in background)
      cacheDataForOffline(orgData, userData);
      
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Set session directly (for signup, accept invite, etc.)
  const setSession = (token, userData, orgData) => {
    localStorage.setItem('session_token', token);
    setSessionToken(token);
    setUser(userData);
    setOrganization(orgData);
    // Check if needs onboarding
    const needsOnboard = !orgData.service_types_enabled || orgData.service_types_enabled.length === 0;
    setNeedsOnboarding(needsOnboard);
  };

  const onboard = async (orgData, userName, userEmail) => {
    try {
      setIsLoading(true);
      const response = await api.post(
        `/auth/onboard?user_email=${encodeURIComponent(userEmail)}&user_name=${encodeURIComponent(userName)}`,
        orgData
      );
      
      const { session_token, user: userData, organization: orgDataResponse } = response.data;
      
      localStorage.setItem('session_token', session_token);
      setSessionToken(session_token);
      setUser(userData);
      setOrganization(orgDataResponse);
      setNeedsOnboarding(false);
      
      toast.success('Welcome to ContractorOS!', { 
        description: 'Your account has been created',
        id: 'onboard-success'
      });
      navigate('/dashboard', { replace: true });
      
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('session_token');
    setSessionToken(null);
    setUser(null);
    setOrganization(null);
    setPermissions(null);
    navigate('/login', { replace: true });
    toast.success('Logged out successfully', { id: 'logout-success' });
  }, [navigate]);

  const updateOrganization = (updates) => {
    setOrganization(prev => ({ ...prev, ...updates }));
  };

  // Check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    // Admins and owners always have all permissions
    if (user?.role === 'admin' || user?.role === 'owner') {
      return true;
    }
    // Check the permissions object
    if (permissions && typeof permissions === 'object') {
      return permissions[permission] === true;
    }
    // Default to false if no permissions loaded
    return false;
  }, [user, permissions]);

  // Refresh session to get updated permissions
  const refreshSession = useCallback(async () => {
    if (!sessionToken) return;
    loadingSessionRef.current = false; // Reset the ref to allow reload
    await loadSession();
  }, [sessionToken]);

  const value = {
    user,
    organization,
    permissions,
    setOrganization,
    sessionToken,
    isAuthenticated: !!user && !!organization,
    isLoading,
    needsOnboarding,
    setNeedsOnboarding,
    login,
    setSession,
    requestMagicLink,
    verifyMagicLink,
    onboard,
    logout,
    updateOrganization,
    loadSession,
    hasPermission,
    refreshSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
